import { useState } from 'react';
import type { Vessel, VesselType, HydrostaticRow, Tank } from '../types';
import { validateVesselName, validateIMO, validateLBP } from '../lib/security';
import { HelpButton } from './HelpModal';
import OCRUpload from './OCRUpload';
import { parseOCRHydrostatic } from '../lib/ocr';

import { X, Ship, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';

interface VesselFormProps {
  onSave: (vessel: Omit<Vessel, 'id' | 'created_at'>) => void;
  onCancel: () => void;
  onHelpId?: (id: string | null) => void;
}

const VESSEL_TYPES: VesselType[] = ['general_cargo','bulk_carrier','container','tanker','chemical','lpg','lng'];

export default function VesselForm({ onSave, onCancel, onHelpId }: VesselFormProps) {
  const [name, setName] = useState('');
  const [imo, setImo] = useState('');
  const [type, setType] = useState<VesselType>('general_cargo');

  // Dimensions
  const [lbp, setLbp] = useState('');
  const [loa, setLoa] = useState('');
  const [beam, setBeam] = useState('');
  const [depth, setDepth] = useState('');

  // Tonnage
  const [dwt, setDwt] = useState('');
  const [lightShip, setLightShip] = useState('');
  const [grosTonnage, setGrosTonnage] = useState('');
  const [netTonnage, setNetTonnage] = useState('');

  // Drafts
  const [summerDraft, setSummerDraft] = useState('');
  const [winterDraft, setWinterDraft] = useState('');
  const [tropicalDraft, setTropicalDraft] = useState('');
  const [fwDraft, setFwDraft] = useState('');

  // Tables
  const [hydroText, setHydroText] = useState('');
  const [tankText, setTankText] = useState('');

  const [errors, setErrors] = useState<string[]>([]);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const setHelpId = onHelpId || (() => {});
  const [fullTable, setFullTable] = useState(true);


  const parseHydro = (): HydrostaticRow[] => {
    try {
      const rows = hydroText.trim().split('\n').filter(Boolean);
      return rows.map((r) => {
        const [draft, disp, tpc, mtc, lcf] = r.split(/[,\t]+/).map((s) => parseFloat(s.trim()));
        return { draft, displacement: disp, tpc: tpc || 0, mtc: mtc || 0, lcf: lcf || 0 };
      }).filter((r) => !isNaN(r.draft) && !isNaN(r.displacement));
    } catch { return []; }
  };

  const parseTanks = (): Tank[] => {
    try {
      return JSON.parse(tankText || '[]');
    } catch { return []; }
  };

  const handleOCRResult = (result: any) => {
    if (result?.structured) {
      const rows = parseOCRHydrostatic(result.structured);
      if (rows.length > 0) {
        setHydroText(rows.map(r => `${r.draft},${r.displacement},${r.tpc},${r.mtc},${r.lcf}`).join('\n'));
      }
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errs: string[] = [];
    try { validateVesselName(name); } catch (e) { errs.push(e instanceof Error ? e.message : 'Invalid name'); }
    try { validateIMO(imo); } catch (e) { errs.push(e instanceof Error ? e.message : 'Invalid IMO'); }
    try { validateLBP(parseFloat(lbp || '0')); } catch (e) { errs.push(e instanceof Error ? e.message : 'Invalid LBP'); }
    if (fullTable && parseHydro().length === 0 && parseTanks().length === 0) errs.push('Add hydrostatic table or tank tables');

    if (errs.length > 0) { setErrors(errs); return; }

    onSave({
      name,
      imo: imo || undefined,
      type,
      lbp: parseFloat(lbp) || undefined,
      loa: parseFloat(loa) || undefined,
      beam: parseFloat(beam) || undefined,
      depth: parseFloat(depth) || undefined,
      dwt: parseFloat(dwt) || undefined,
      light_ship: parseFloat(lightShip || '0') || undefined,
      gros_tonnage: parseFloat(grosTonnage) || undefined,
      net_tonnage: parseFloat(netTonnage) || undefined,
      summer_draft: parseFloat(summerDraft) || undefined,
      winter_draft: parseFloat(winterDraft) || undefined,
      tropical_draft: parseFloat(tropicalDraft) || undefined,
      fw_draft: parseFloat(fwDraft) || undefined,
      hydrostatic: fullTable ? parseHydro() : [],
      tank_tables: parseTanks(),
      constants: { bunkers: 0, fw: 0, ballast: 0, stores: 0, slops: 0, others: 0 },
    });
  };

  const cleanImo = (val: string) => val.replace(/[^0-9]/g, '').slice(0, 7);

  const numInput = (label: string, value: string, onChange: (v: string) => void, placeholder?: string) => (
    <div>
      <label className="label-premium">{label}</label>
      <input type="number" step="0.01" className="input-premium" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder || '0.00'} />
    </div>
  );

  return (
    <div className="animate-fade-in space-y-6">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 rounded-xl bg-ocean-600/20 flex items-center justify-center">
          <Ship className="w-5 h-5 text-ocean-400" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-navy-100">New Vessel</h2>
          <p className="text-xs text-navy-400">All data is saved for future surveys</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        {errors.length > 0 && (
          <div className="bg-rose-500/10 border border-rose-500/20 rounded-xl p-3 space-y-1">
            {errors.map((e, i) => <p key={i} className="text-xs text-rose-400 flex items-center gap-2"><X className="w-3 h-3" /> {e}</p>)}
          </div>
        )}

        {/* Basic Info */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="label-premium">Vessel Name *</label>
            <input className="input-premium" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. COMANAV Atlas" required />
          </div>
          <div>
            <label className="label-premium">IMO Number</label>
            <input className="input-premium" value={imo} onChange={(e) => setImo(cleanImo(e.target.value))} placeholder="7 digits" />
          </div>
          <div>
            <label className="label-premium">Vessel Type</label>
            <select className="input-premium" value={type} onChange={(e) => setType(e.target.value as VesselType)}>
              {VESSEL_TYPES.map((t) => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
            </select>
          </div>
          <div>
            <label className="label-premium">DWT (Deadweight Tonnage)</label>
            <input className="input-premium" type="number" step="0.01" value={dwt} onChange={(e) => setDwt(e.target.value)} placeholder="Metric Tons" />
          </div>
        </div>

        {/* Dimensions */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {numInput('LBP (m)', lbp, setLbp, 'Length Between Perpendiculars')}
          {numInput('LOA (m)', loa, setLoa, 'Length Over All')}
          {numInput('Beam (m)', beam, setBeam, 'Width')}
          {numInput('Depth (m)', depth, setDepth, 'Moulded Depth')}
        </div>

        {/* Tonnage */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          {numInput('Light Ship (MT)', lightShip, setLightShip)}
          {numInput('Gross Tonnage', grosTonnage, setGrosTonnage)}
          {numInput('Net Tonnage', netTonnage, setNetTonnage)}
        </div>

        {/* Drafts */}
        <div>
          <button type="button" onClick={() => setShowAdvanced(!showAdvanced)} className="flex items-center gap-2 text-sm text-ocean-400 hover:text-ocean-300 mb-3">
            {showAdvanced ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            Load Line Drafts
          </button>
          {showAdvanced && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 animate-fade-in">
              {numInput('Summer Draft (m)', summerDraft, setSummerDraft)}
              {numInput('Winter Draft (m)', winterDraft, setWinterDraft)}
              {numInput('Tropical Draft (m)', tropicalDraft, setTropicalDraft)}
              {numInput('FW Draft (m)', fwDraft, setFwDraft)}
            </div>
          )}
        </div>

        {/* LCF Warning */}
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-3 flex gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
          <div className="text-xs text-amber-300">
            <strong>LCF Convention:</strong> This app uses European convention where <strong>positive LCF = AFT of midship</strong>. Some Asian shipyards use the opposite. If your table uses negative=aft, multiply all LCF values by −1 before uploading.
          </div>
        </div>

        {/* Hydrostatic Table */}
        <div className="flex items-center gap-3 mb-3">
          <label className="flex items-center gap-2 text-sm text-navy-300 cursor-pointer">
            <input type="radio" checked={fullTable} onChange={() => setFullTable(true)} className="accent-ocean-500" />
            Full Table Upload
          </label>
          <label className="flex items-center gap-2 text-sm text-navy-300 cursor-pointer">
            <input type="radio" checked={!fullTable} onChange={() => setFullTable(false)} className="accent-ocean-500" />
            Direct Values (per survey)
          </label>
        </div>

        {fullTable && (
        <div>
          <label className="label-premium flex items-center">
            Hydrostatic Table (CSV/TSV) *
            <HelpButton helpId="hydrostatic" onClick={setHelpId} />

          </label>

          <OCRUpload
            type="hydrostatic"
            label="📷 Take a photo of the hydrostatic table — AI will extract data"
            onResult={handleOCRResult}
          />

          <div className="relative my-3">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-navy-700/50"></div>
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="bg-navy-900 px-2 text-navy-500">or paste manually</span>
            </div>
          </div>

          <textarea
            className="input-premium font-mono text-xs h-32"
            value={hydroText}
            onChange={(e) => setHydroText(e.target.value)}
            placeholder={`draft,displacement,tpc,mtc,lcf\n2.0,5000,12.5,145,2.1\n2.5,6200,13.0,150,1.8`}
          />
          <p className="text-[10px] text-navy-500 mt-1">Columns: draft, displacement, tpc, mtc, lcf. LCF must be from midship with European sign convention.</p>
        </div>

        )}

        {!fullTable && (
          <div className="bg-ocean-600/5 border border-ocean-600/10 rounded-xl p-4">
            <p className="text-sm text-ocean-400 font-medium mb-2">Direct Values Mode — Selected</p>
            <p className="text-xs text-navy-400 mb-2">✓ No table upload needed. Hydrostatic values (displacement, TPC, MTC, LCF) will be entered during each survey.
✓ After creating this vessel, go to "New Survey" → select this vessel → enter the 4 values below draft readings.</p>
            <p className="text-xs text-navy-500">After creating this vessel, go to "New Survey" → select this vessel → the hydrostatic input fields will appear below the draft readings.</p>
          </div>
        )}

        {/* Tank Tables */}
        <div>
          <label className="label-premium flex items-center">
            Tank Tables (JSON) — for tankers/LPG
            <HelpButton helpId="tank-table" onClick={setHelpId} />

          </label>
          <textarea
            className="input-premium font-mono text-xs h-32"
            value={tankText}
            onChange={(e) => setTankText(e.target.value)}
            placeholder={`[{\"tank_no\":\"1P\",\"name\":\"Port Wing\",\"capacity\":500,\"table\":[{\"ullage\":0,\"volume\":0},{\"ullage\":10,\"volume\":250}]}]`}
          />
        </div>

        <div className="flex gap-3 pt-2">
          <button type="button" onClick={onCancel} className="btn-secondary flex-1">Cancel</button>
          <button type="submit" className="btn-primary flex-1">Save Vessel</button>
        </div>
      </form>
    </div>
  );
}
